// src/components/CookAI/Testimonials/TestimonialsSection.tsx
import React from "react";
import { Star } from "lucide-react";
import { ColorTheme } from "../../../themes/colors";
import Reveal from "../common/Reveal";
import { useLanguage } from "../../../i18n/LanguageContext";

interface TestimonialsSectionProps {
    theme: ColorTheme;
    isMobile: boolean;
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ theme, isMobile }) => {
    const { t } = useLanguage();

    return (
        <section
            id="testimonials"
            style={{
                padding: isMobile ? "4rem 1.25rem" : "6rem 2rem",
                background: theme.darkBackground,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Ambient glows */}
            <div aria-hidden style={{
                position: "absolute",
                top: "-180px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "700px",
                height: "380px",
                background: `radial-gradient(ellipse at center, ${theme.primary}20 0%, transparent 70%)`,
                pointerEvents: "none",
            }} />
            <div aria-hidden style={{
                position: "absolute",
                bottom: "-200px",
                right: "-120px",
                width: "500px",
                height: "400px",
                background: `radial-gradient(ellipse at center, ${theme.secondary}18 0%, transparent 70%)`,
                pointerEvents: "none",
            }} />

            <div style={{ maxWidth: "1180px", margin: "0 auto", position: "relative" }}>
                <Reveal>
                    <h2 style={{
                        fontSize: "clamp(2.2rem, 5vw, 3rem)",
                        fontWeight: 800,
                        color: "#FFFFFF",
                        marginBottom: "1.25rem",
                        textAlign: "center",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.12,
                    }}>
                        {t.testimonials.sectionTitle}
                    </h2>
                    <p style={{
                        fontSize: "1.15rem",
                        color: "rgba(255,255,255,0.65)",
                        maxWidth: "42rem",
                        margin: "0 auto 3.5rem auto",
                        textAlign: "center",
                        lineHeight: 1.65,
                    }}>
                        {t.testimonials.sectionSubtitle}
                    </p>
                </Reveal>

                <div style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                    gap: "1.5rem",
                }}>
                    {t.testimonials.items.map((item, i) => (
                        <Reveal key={i} delay={isMobile ? 0 : i * 120} style={{ height: "100%" }}>
                            <div
                                style={{
                                    height: "100%",
                                    background: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    borderRadius: "22px",
                                    padding: "1.9rem",
                                    display: "flex",
                                    flexDirection: "column",
                                    position: "relative",
                                    backdropFilter: "blur(8px)",
                                    transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.35s ease, background 0.35s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-6px)";
                                    e.currentTarget.style.borderColor = `${theme.primary}66`;
                                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                                    e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                                }}
                            >
                                {/* Oversized quote mark */}
                                <div aria-hidden style={{
                                    position: "absolute",
                                    top: "6px",
                                    right: "18px",
                                    fontSize: "5rem",
                                    fontFamily: "'Bricolage Grotesque', Georgia, serif",
                                    color: `${theme.primary}30`,
                                    lineHeight: 1,
                                    userSelect: "none",
                                }}>
                                    ”
                                </div>

                                <div style={{ display: "flex", gap: "3px", marginBottom: "1.1rem" }}>
                                    {[...Array(5)].map((_, s) => (
                                        <Star key={s} size={15} fill="#FBBF24" color="#FBBF24" />
                                    ))}
                                </div>

                                <p style={{
                                    color: "rgba(255,255,255,0.88)",
                                    lineHeight: 1.7,
                                    fontSize: "1rem",
                                    flexGrow: 1,
                                    marginBottom: "1.5rem",
                                }}>
                                    {item.quote}
                                </p>

                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <div style={{
                                        width: "42px",
                                        height: "42px",
                                        borderRadius: "50%",
                                        background: i === 1
                                            ? `linear-gradient(135deg, ${theme.secondary}, ${theme.primary})`
                                            : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#FFFFFF",
                                        fontWeight: 800,
                                        fontSize: "0.95rem",
                                        fontFamily: "'Bricolage Grotesque', sans-serif",
                                        flexShrink: 0,
                                    }}>
                                        {item.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div style={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.95rem" }}>
                                            {item.name}
                                        </div>
                                        <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem" }}>
                                            {item.role}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;
